import { useCallback, useRef, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import { callLookupProductPrice } from "@data/functions/lookupProductPrice";

export const looksLikeUrl = (s) => /^(https?:\/\/)?[^\s/]+\.[^\s]{2,}/i.test((s || "").trim());
export const normalizeUrl = (s) => {
  const t = (s || "").trim();
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};

/**
 * Fetches a product link's price via the lookupProductPrice function and
 * writes it onto the item. Tracks per-item pending/error state for the UI.
 */
export default function usePriceLookup(update) {
  const { activeProject } = useProjects();
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const inFlight = useRef(new Set());

  const lookup = useCallback(
    async (item, { overwrite = false } = {}) => {
      if (!activeProject || !looksLikeUrl(item.link)) return;
      const url = normalizeUrl(item.link);
      const key = `${item.id}|${url}`;
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setPending((p) => ({ ...p, [item.id]: true }));
      setErrors((e) => ({ ...e, [item.id]: null }));
      try {
        const res = await callLookupProductPrice({
          projectId: activeProject.id,
          url,
          itemName: item.name || undefined,
        });
        const patch = {
          priceLookup: {
            source: res.source,
            confidence: res.confidence,
            note: res.note,
            fetchedAt: Date.now(),
            url,
          },
        };
        if (res.price != null) {
          // Don't clobber a price the user typed unless they asked to refresh.
          if (overwrite || !item.unitPrice) patch.unitPrice = res.price;
          if (res.shipping != null && (overwrite || !item.shipping)) patch.shipping = res.shipping;
          if (!item.name?.trim() && res.productName) patch.name = res.productName;
        } else {
          setErrors((e) => ({ ...e, [item.id]: res.note || "No price found — enter it manually." }));
        }
        update(item.id, patch);
      } catch (err) {
        setErrors((e) => ({ ...e, [item.id]: err?.message || "Price lookup failed." }));
      } finally {
        inFlight.current.delete(key);
        setPending((p) => ({ ...p, [item.id]: false }));
      }
    },
    [activeProject, update]
  );

  return { lookup, pending, errors };
}
